/**
 * Conteúdo base da Política de Privacidade (/privacidade).
 * Rascunho inicial — edite aqui para atualizar textos e a data de revisão.
 * Sem CMS neste momento.
 */

export const PRIVACIDADE_LAST_UPDATED_ISO = '2026-07-28';

/** Formatação pt-BR da data de atualização. */
export function formatPrivacidadeLastUpdated(
  isoDate: string = PRIVACIDADE_LAST_UPDATED_ISO,
  locale = 'pt-BR'
): string {
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(parsed);
}

export type PrivacidadeSection = {
  id: string;
  navLabel: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
};

export const PRIVACIDADE_INTRO =
  'A sua privacidade é importante para nós. Esta Política de Privacidade explica, de forma geral, como informações podem ser tratadas durante a utilização do xat Ajuda e dos recursos disponibilizados pelo portal.';

export const PRIVACIDADE_SECTIONS: PrivacidadeSection[] = [
  {
    id: 'sobre',
    navLabel: 'Sobre esta Política',
    title: '1. Sobre esta Política',
    paragraphs: [
      'Esta Política de Privacidade descreve, em linguagem acessível, como informações podem ser tratadas quando você acessa ou utiliza o xat Ajuda.',
      'O objetivo é oferecer transparência sobre práticas gerais relacionadas à navegação e ao uso dos recursos disponíveis no portal. O texto pode ser atualizado conforme a evolução do serviço e a revisão jurídica aplicável.',
    ],
  },
  {
    id: 'informacoes-coletadas',
    navLabel: 'Informações coletadas',
    title: '2. Informações que podem ser coletadas',
    paragraphs: [
      'Dependendo de como você utiliza o portal, e quando aplicável, podem ser coletadas ou processadas informações em categorias como as descritas abaixo. Nem todas as categorias se aplicam necessariamente a todos os visitantes.',
    ],
    bullets: [
      'Informações fornecidas diretamente pelo usuário — por exemplo, quando você envia um pedido de suporte ou preenche formulários disponibilizados no portal.',
      'Informações relacionadas à navegação — como páginas visitadas, interações gerais com o conteúdo e preferências de uso, quando aplicável.',
      'Informações técnicas do dispositivo e do navegador — por exemplo, tipo de dispositivo, navegador, idioma e dados técnicos semelhantes necessários ao funcionamento do serviço.',
      'Informações relacionadas ao uso do portal — como padrões gerais de utilização que ajudem a compreender o desempenho e a melhoria da experiência.',
    ],
  },
  {
    id: 'como-utilizamos',
    navLabel: 'Como utilizamos',
    title: '3. Como utilizamos as informações',
    paragraphs: [
      'Quando aplicável, informações podem ser utilizadas para finalidades legítimas relacionadas à operação do xat Ajuda, incluindo:',
    ],
    bullets: [
      'Disponibilizar, manter e melhorar o portal e seus conteúdos.',
      'Responder a solicitações de suporte ou contacto, quando existirem canais para isso.',
      'Identificar e corrigir problemas técnicos.',
      'Melhorar a experiência de navegação e a organização do conteúdo.',
      'Ajudar a garantir a segurança do serviço e a prevenção de abusos.',
      'Cumprir obrigações legais aplicáveis, quando for o caso.',
    ],
  },
  {
    id: 'cookies',
    navLabel: 'Cookies',
    title: '4. Cookies e tecnologias semelhantes',
    paragraphs: [
      'Cookies e tecnologias semelhantes são pequenos ficheiros ou mecanismos armazenados no seu dispositivo que podem ajudar o site a funcionar, lembrar preferências ou compreender, de forma agregada, como o portal é utilizado.',
      'Quando aplicável, essas tecnologias podem ser usadas para funcionalidades essenciais, preferências de interface, desempenho ou análise de uso. As práticas concretas podem variar conforme as funcionalidades ativas no portal.',
      'A maioria dos navegadores permite controlar ou bloquear determinados cookies nas definições. Tenha em conta que desativar cookies essenciais pode afetar o funcionamento de algumas partes do serviço.',
    ],
  },
  {
    id: 'compartilhamento',
    navLabel: 'Compartilhamento',
    title: '5. Compartilhamento de informações',
    paragraphs: [
      'Informações podem ser partilhadas quando for necessário para a operação do serviço, para cumprir obrigações legais, para proteger direitos e segurança, ou noutras situações legítimas previstas na legislação aplicável.',
      'Não afirmamos neste rascunho a inexistência absoluta de partilha de dados. Qualquer partilha deve ocorrer de forma proporcional às finalidades descritas e às exigências legais vigentes, sujeita a revisão posterior desta Política.',
    ],
  },
  {
    id: 'terceiros',
    navLabel: 'Serviços de terceiros',
    title: '6. Serviços de terceiros',
    paragraphs: [
      'O portal pode depender ou utilizar serviços de terceiros para determinadas funcionalidades (por exemplo, infraestrutura, entrega de conteúdos ou recursos técnicos).',
      'Esses serviços podem ter as suas próprias políticas de privacidade e termos de uso. Recomendamos a leitura desses documentos quando utilizar recursos fornecidos por terceiros a partir do xat Ajuda.',
    ],
  },
  {
    id: 'seguranca',
    navLabel: 'Segurança',
    title: '7. Segurança',
    paragraphs: [
      'Podem ser adotadas medidas razoáveis para proteger informações contra acesso não autorizado, perda, alteração ou uso indevido, de acordo com o contexto técnico e operacional do portal.',
      'Nenhum método de transmissão ou armazenamento é totalmente seguro. Por isso, não é possível garantir segurança absoluta, embora sejam feitos esforços razoáveis para reduzir riscos.',
    ],
  },
  {
    id: 'retencao',
    navLabel: 'Retenção',
    title: '8. Retenção de informações',
    paragraphs: [
      'Quando informações forem mantidas, isso pode ocorrer pelo período necessário para cumprir as finalidades para as quais foram coletadas, atender obrigações legais, resolver disputas ou questões relacionadas ao serviço, ou outras necessidades legítimas compatíveis com a legislação aplicável.',
      'Os prazos concretos podem variar conforme o tipo de informação e o contexto de utilização.',
    ],
  },
  {
    id: 'direitos',
    navLabel: 'Direitos e escolhas',
    title: '9. Direitos e escolhas do usuário',
    paragraphs: [
      'Dependendo da legislação aplicável — incluindo, no Brasil, o enquadramento da Lei Geral de Proteção de Dados (LGPD), quando for o caso — você pode ter direitos relacionados aos seus dados pessoais.',
      'De forma geral, e sujeito às condições legais, esses direitos podem incluir:',
    ],
    bullets: [
      'Acesso a informações sobre o tratamento, quando aplicável.',
      'Correção de dados incompletos, inexatos ou desatualizados.',
      'Atualização de informações que você tenha fornecido.',
      'Solicitação de exclusão, quando a legislação o permitir.',
      'Outras solicitações previstas na legislação vigente.',
    ],
  },
  {
    id: 'criancas',
    navLabel: 'Crianças e adolescentes',
    title: '10. Privacidade de crianças e adolescentes',
    paragraphs: [
      'O xat Ajuda não deve ser utilizado para coletar intencionalmente informações pessoais de crianças em desacordo com a legislação aplicável.',
      'Se tomar conhecimento de que informações de uma criança foram fornecidas de forma inadequada, contacte-nos pelos canais indicados nesta Política para que possamos analisar a situação. Este texto é um rascunho e permanece sujeito a revisão jurídica.',
    ],
  },
  {
    id: 'alteracoes',
    navLabel: 'Alterações',
    title: '11. Alterações nesta Política',
    paragraphs: [
      'Esta Política de Privacidade pode ser atualizada periodicamente para refletir mudanças no portal, nas práticas de tratamento de informações ou em requisitos legais.',
      'A data da última atualização aparece no topo desta página. Recomendamos consultar este documento de tempos a tempos para se manter informado sobre a versão vigente.',
    ],
  },
  {
    id: 'contato',
    navLabel: 'Contato',
    title: '12. Contato',
    paragraphs: [
      'Se tiver dúvidas ou solicitações relacionadas a esta Política de Privacidade, utilize os canais de contacto já disponíveis no portal — por exemplo, a página da equipe e as páginas institucionais — ou consulte os guias de suporte publicados no xat Ajuda.',
    ],
  },
];
